/**
 * Categories API Client
 * Handles all category-related API calls
 */

import { apiClient } from './client';

export interface Category {
  id: string;
  name: string;           // slug: "blog", "technology"
  display_name: string;   // display: "Blog", "Technology"
  created_at: number;
}

export interface CategoryWithStats extends Category {
  article_count: number;
}

export const categoriesApi = {
  /**
   * Get all categories for the current tenant
   */
  getCategories: async (): Promise<Category[]> => {
    const response = await apiClient.get('/api/categories');
    return response.data;
  },

  /**
   * Create a new category
   * @param name - Category name (will be slugified)
   * @param displayName - Display name for UI
   */
  createCategory: async (name: string, displayName: string): Promise<Category> => {
    const response = await apiClient.post('/api/categories', { name, displayName });
    return response.data;
  },

  /**
   * Update a category's display name
   * @param id - Category ID
   * @param displayName - New display name
   */
  updateCategory: async (id: string, displayName: string): Promise<void> => {
    await apiClient.put(`/api/categories/${id}`, { displayName });
  },

  /**
   * Delete a category (only if not in use)
   * @param id - Category ID
   */
  deleteCategory: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/categories/${id}`);
  },

  /**
   * Get category usage statistics
   */
  getCategoryStats: async (): Promise<CategoryWithStats[]> => {
    const response = await apiClient.get('/api/categories/stats');
    return response.data;
  }
};
